import type { Lead } from 'providers/LeadProvider';

/**
 * Determines whether the provided lead has a confirmation file associated with its membership.
 *
 * Checks if the `lead` object contains a `memberships.confirmation.file` property
 * with either an `id` or a `name` defined. If the file has a name, it means it's local
 * and email validation was skipped.
 *
 * @param lead - The lead object to check for a confirmation file.
 * @returns `true` if a confirmation file exists (by `id` or `name`), otherwise `false`.
 */
function leadHasConfirmationFile(lead?: Lead) {
  return (
    !!lead?.memberships?.confirmation?.file?.id ||
    // If file has name means it's local and email validation was skipped
    !!lead?.memberships?.confirmation?.file?.name
  );
}

/**
 * Determines if the user has submitted the pricing step.
 *
 * Checks that the lead has a memberships object with a known pricingModel
 * and a confirmation file (uploaded or local).
 *
 * @param lead - An optional `Lead` object containing membership details.
 * @returns `true` if the pricing step has been completed, otherwise `false`.
 */
export function hasUserSubmittedPricingStep(lead?: Lead) {
  const pricingModel = lead?.memberships?.pricingModel;

  if (!pricingModel) return false;

  return leadHasConfirmationFile(lead);
}

/**
 * Determines if a lead has a Google Place ID associated with it.
 *
 * @param lead - An optional `Lead` object to check for Google Place ID.
 * @returns `true` if the lead has a Google Place ID, otherwise `false`.
 */
export function leadHasPlaceId(lead?: Lead) {
  return Boolean(lead?.googlePlaceId);
}
/**
 * Determines if a lead has a tax ID (tax identification number).
 *
 * @param lead - An optional `Lead` object to check for tax ID.
 * @returns `true` if the lead has a tax ID, otherwise `false`.
 */
export function leadHasTaxId(lead?: Lead) {
  return !!lead?.taxId;
}
